import { inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Store } from '@ngrx/store';
import { filter, interval } from 'rxjs';

import { appStateActions } from '@blaze-shared/app-state-data-access';
import { urlUtils } from '@blaze-shared/platform-util';

@Injectable({ providedIn: 'root' })
export class SwService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly store = inject(Store);

  registerServiceWorker(): void {
    if (!this.swUpdate.isEnabled || !urlUtils.isPublishedAppUrl()) {
      return;
    }

    // Check for updates every 6 hours.
    interval(6 * 60 * 60 * 1000).subscribe(() =>
      this.swUpdate
        .checkForUpdate()
        .then(() => console.log('Checking for service worker update'))
        .catch((err) => console.error('Failed to check for service worker update', err)),
    );

    this.swUpdate.versionUpdates
      .pipe(
        filter(() => urlUtils.isPublishedAppUrl()),
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      )
      .subscribe(() => {
        // Prompt the user to reload the page to apply the update.
        this.store.dispatch(
          appStateActions.setNewVersionAction({ newVersionAction: 'force-reload' }),
        );
      });
  }

  /**
   * Unregisters the service worker for a given project. This is usually called when the service
   * worker was registered for a non-PWA app. At the time of registration we have no information
   * whether app is a PWA app or not so we are forced to register the service worker and
   * unregister it later once we have that information.
   */
  async removeServiceWorker(projectName: string): Promise<boolean> {
    // It's possible for the service worker to not be registered when user requests service worker
    // removal. If that's the case, wait for the service worker to be ready and then unregister
    // project which was requested to be removed.
    if ('serviceWorker' in navigator) {
      // Do not await this. Let it run in the background so it catches any late registrations
      // without blocking app initialization.
      navigator.serviceWorker.ready.then((registration) => {
        const projectNameFromRegistration = urlUtils.getPublishedProjectName(
          registration?.scope || '',
        );
        if (projectNameFromRegistration === projectName) {
          console.log(`Removing service worker for project ${projectNameFromRegistration}`);
          registration.unregister();
        }
      });
    }

    if (!('serviceWorker' in navigator)) {
      return Promise.resolve(true);
    }

    console.log(
      `Requesting removal of service worker for project ${projectName}. (Project not set up as a PWA app)`,
    );
    return navigator.serviceWorker.getRegistrations().then((registrations) => {
      let unregistered = false;
      for (const registration of registrations) {
        const projectNameFromRegistration = urlUtils.getPublishedProjectName(
          registration?.scope || '',
        );

        if (projectNameFromRegistration === projectName) {
          const worker = registration.installing || registration.waiting || registration.active;

          // If the worker is currently installing or activating, wait until it finishes
          if (worker && (worker.state === 'installing' || worker.state === 'activating')) {
            worker.addEventListener('statechange', () => {
              if (worker.state === 'activated') {
                registration.unregister();
              }
            });
          } else {
            // If it's already active (or redundant), it's safe to unregister immediately
            registration.unregister();
          }

          unregistered = true;
        }
      }
      return unregistered;
    });
  }
}
